import React, { useMemo } from "react";

import { AnimatePresence, motion } from "framer-motion";

import { useCustomerBreakdownConfigs } from "@components/data/customer-breakdown-configs";
import { VirtualizedTable } from "@components/reports/virtualized-table";
import { LoadingSlide, TitleSlide } from "@components/title-slide-view";
import { useUser } from "@components/user-context";
import { buildTable } from "@features/customer-ltv-v2/components/main/utils";
import {
  ROWS_PER_PAGE,
  type MetricName,
} from "@features/customer-ltv-v2/constants";
import { useCustomerLtvV2 } from "@features/customer-ltv-v2/hooks";
import { Paginator } from "@shared/paginator";

import { Toolbar } from "./toolbar";

export const Main = () => {
  const { user } = useUser();
  const { value: breakdownConfigs } = useCustomerBreakdownConfigs();
  const { data, loading, filters, setFilters } = useCustomerLtvV2();

  const table = useMemo(
    () =>
      buildTable({
        user,
        breakdownConfigs,
        breakdown: filters.breakdown?.key,
        metric: filters.metric as MetricName,
        rollupMode: filters.rollupMode,
        sumMode: filters.sumMode,
        data,
      }),
    [data, filters],
  );

  if (!table) {
    return (
      <div className="h-2/3">
        {loading ? (
          <LoadingSlide />
        ) : (
          <TitleSlide>
            <p className="mt-6">Customize your report on the left-hand side.</p>
          </TitleSlide>
        )}
      </div>
    );
  }

  return (
    <div className="grid grid-rows-[110px_auto_min-content] h-full p-4">
      <div>
        <Toolbar />
      </div>
      <AnimatePresence>
        <motion.div
          className="pt-4"
          key={JSON.stringify(filters)}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2, ease: "easeIn" }}
        >
          {loading ? (
            <LoadingSlide className="h-full" />
          ) : (
            <VirtualizedTable
              autoHeight={true}
              allowSortingByLastDimensionColumn={true}
              frozenRows={table.stickyRows}
              rows={table.rows}
              dimensionColumn={table.dimensionColumn}
              factColumns={table.factColumns}
              sortBy={{ key: filters.sort.column, order: filters.sort.order }}
              onColumnSort={(column, order) =>
                setFilters({ sort: { column, order } })
              }
            />
          )}
        </motion.div>
      </AnimatePresence>
      <div className="flex justify-end mt-4">
        <Paginator
          disabled={loading}
          total={data?.totalRows || 0}
          perPage={ROWS_PER_PAGE}
          page={filters.page}
          onChange={(page) => setFilters({ page })}
        />
      </div>
    </div>
  );
};
